import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { PageLayout } from 'components/PageLayout';
import { useAuthState } from 'state/useAuthState';
import { OrganizationProFeaturesDownstate } from 'features/organizations/components/OrganizationProFeaturesDownstate';
import { Redirect, useLocation } from 'react-router-dom';
import { isIframe } from 'shared/utils';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import { MembershipPlanProvider } from 'features/membership/components/MembershipPlanProvider';
import { useSupportUrlState } from 'features/membership/hooks/useSupportUrlState';
import { SupportUsContent } from './SupportUsContent';
import { EmbeddedSupportUs } from './EmbeddedSupportUs';

const Content = (): JSX.Element => {
  return (
    <MembershipPlanProvider>
      <SupportUsContent />
    </MembershipPlanProvider>
  );
};

export const SupportUs = (): JSX.Element => {
  const { t } = useTranslation();
  const { isOrganizationUser, hasActiveMembership, isProMember } =
    useAuthState();
  const location = useLocation<{ fromRoute?: string }>();
  const { source } = useSupportUrlState();
  const posthog = usePostHog();

  useEffect(() => {
    if (source) {
      posthog.capture('viewed_support_us', { source });
    }
  }, [posthog, source]);

  const returnRoute = location.state?.fromRoute || '/';

  if (isIframe()) {
    return (
      <PageLayout
        data-cname="SupportUsPage"
        headerBarProps={{ title: t('supportUs.title'), action: returnRoute }}
      >
        <Helmet>
          <title>{t('supportUs.title')}</title>
        </Helmet>
        <SimplePageLayout maxWidth="sm" bgcolor="background.membership">
          <EmbeddedSupportUs />
        </SimplePageLayout>
      </PageLayout>
    );
  }

  if (isOrganizationUser && hasActiveMembership && isProMember) {
    return <Redirect to="/" />;
  }

  if (!isOrganizationUser && hasActiveMembership) {
    return <Redirect to="/membership/" />;
  }

  const ContentComponent = isOrganizationUser
    ? OrganizationProFeaturesDownstate
    : Content;

  const title = isOrganizationUser ? '' : t('supportUs.title');

  return (
    <PageLayout
      data-cname="SupportUsPage"
      headerBarProps={{ title, action: returnRoute }}
    >
      <Helmet>
        <title>{t('supportUs.title')}</title>
      </Helmet>
      <ContentComponent />
    </PageLayout>
  );
};
